import { parseStringWithOptions, StringParseOptions } from './string-utils';

export interface OpenQuestionComparisonSettings {
  normalizeCapitalization: boolean;
  normalizeDiacritics: boolean;
}

/**
 * Compares two open question answers based on the provided settings.
 * Returns true if the answers should be considered equivalent.
 *
 * @param answer1 - First answer to compare
 * @param answer2 - Second answer to compare
 * @param settings - Settings for comparison (capitalization and diacritics)
 * @returns true if answers are equivalent based on settings
 *
 * @example
 * ```typescript
 * const settings = { normalizeCapitalization: true, normalizeDiacritics: true };
 *
 * // These should be equivalent when ignoring capitalization
 * compareOpenQuestionAnswers("LOTR", "lotr", settings); // true
 *
 * // These should be equivalent when ignoring diacritics
 * compareOpenQuestionAnswers("café", "cafe", settings); // true
 *
 * // These should be equivalent when ignoring both
 * compareOpenQuestionAnswers("Café", "cafe", settings); // true
 * ```
 */
export function compareOpenQuestionAnswers(
  answer1: string,
  answer2: string,
  settings: OpenQuestionComparisonSettings
): boolean {
  const options: StringParseOptions = {
    asLowercase: settings.normalizeCapitalization,
    normalizeDiacritics: settings.normalizeDiacritics,
  };

  const normalizedAnswer1 = parseStringWithOptions(answer1, options);
  const normalizedAnswer2 = parseStringWithOptions(answer2, options);

  return normalizedAnswer1 === normalizedAnswer2;
}
